import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Stack, Typography, useTheme } from '@mui/material'

import { StaticMap } from 'components/Cartography'
import { ResponsiveSideContent } from 'components-shared'
import CardPreview, { CardBlockInfo, CardHeader } from 'packages/ui/CardPreview'

import { DEFAULT_SITE_STATE_COLORS, SITE_STATE_CONFIG } from '../constants'
import { BooleanCell, StateCell } from '../Table/config-columns'
import { SiteUnscopeAddress } from '../types'
import useSiteUnscope from '../use-site-unscope'
import CenterVisibility from './CenterVisibility'
import SyncButton from './SyncButton'
import VisibilitySelect from './VisibilitySelect'

const formatAddress = (address: SiteUnscopeAddress | null) => {
  if (!address) return null

  const street = [address.street_number, address.street_name].filter(Boolean).join(' ')
  const city = [address.zipcode, address.city].filter(Boolean).join(' ')
  const formattedAddress = [street, city].filter(Boolean).join(', ')

  return formattedAddress || null
}

const Card = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { focusId } = useParams()
  const navigate = useNavigate()
  const { site, loading, error } = useSiteUnscope(focusId)

  const positions = useMemo(() => {
    if (!site?.location) return []
    return [{ lat: site.location.latitude, lng: site.location.longitude }]
  }, [site?.location])

  const markerColor = (site?.state && SITE_STATE_CONFIG[site.state]?.color) || DEFAULT_SITE_STATE_COLORS.color
  const hasError = Boolean(error) || (!loading && !site)

  const handleClose = () => navigate('..')

  return (
    <ResponsiveSideContent onClose={handleClose} style={{ marginTop: 0 }}>
      <CardPreview
        title={t('ui_elements.card_title')}
        onClose={handleClose}
        loading={loading}
        error={hasError ? t('admin_sites.error') : null}
        padding={2}
      >
        {site && (
          <CardHeader sx={{ width: '100%', minWidth: 0 }}>
            <Stack gap={1.5} width={1} minWidth={0}>
              <Stack alignItems="center" gap={1} minWidth={0}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: theme.palette.grey[800],
                    maxWidth: '100%',
                    textAlign: 'center',
                    wordBreak: 'break-word'
                  }}
                >
                  {site.name}
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="center" gap={1} flexWrap="wrap">
                  {site.state && <StateCell state={site.state} />}
                  <CenterVisibility site={site} />
                </Stack>
              </Stack>
              {positions.length > 0 && (
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <StaticMap
                    site={site}
                    positions={positions}
                    height={192}
                    minWidth="0"
                    color={markerColor}
                  />
                </Box>
              )}
              <CardBlockInfo
                title={t('audin_document.card_details.address')}
                subtitle={formatAddress(site.address)}
              />
              <CardBlockInfo title={t('admin_sites.table.has_active_project')}>
                <BooleanCell value={site.has_active_project} />
              </CardBlockInfo>
              <CardBlockInfo title={t('admin_sites.table.has_process')}>
                <BooleanCell value={site.has_process} />
              </CardBlockInfo>
              <CardBlockInfo title={t('admin_sites.table.visibility_in_center')}>
                <VisibilitySelect siteId={site.id} value={site.visibility_in_center} />
              </CardBlockInfo>
              <SyncButton siteId={site.id} />
            </Stack>
          </CardHeader>
        )}
      </CardPreview>
    </ResponsiveSideContent>
  )
}

export default Card
